import { FC, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  Heading,
  Radio,
  RadioGroup,
  useToast,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';

import {
  useCreateSubscriptionPlanMutation,
  useUpdateSubscriptionPlanMutation,
  useGetSubscriptionPlansQuery,
  useSubscriptionPlanQuery,
} from '../../../services';
import { CenteredSpinner } from '../components';
import { getApiErrorMessage, getBodyFailureMessage } from '../utils';

export const SubscriptionPlan: FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { companyId } = params;
  const status = params.status || 'all';

  const [createSubscriptionPlan] = useCreateSubscriptionPlanMutation();
  const [updateSubscriptionPlan] = useUpdateSubscriptionPlanMutation();
  const { data: subscriptionPlans, isLoading: isGetSubscriptionPlansLoading } =
    useGetSubscriptionPlansQuery();
  const { data: savedSubscriptionPlan, isLoading: isSubscriptionPlanLoading } =
    useSubscriptionPlanQuery(Number(companyId));

  const {
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    control,
    setValue,
  } = useForm<{ subscriptionPlanId: string }>();

  useEffect(() => {
    if (savedSubscriptionPlan?.data?.subscriptionPlan?.id) {
      setValue(
        'subscriptionPlanId',
        String(savedSubscriptionPlan.data.subscriptionPlan.id)
      );
    }
  }, [savedSubscriptionPlan?.data, setValue]);

  const toast = useToast();

  const navigateToPreviousPage = () =>
    navigate(`/companies/${status}/${companyId}`);

  async function onSubmit(data: { subscriptionPlanId: string }) {
    try {
      const result = savedSubscriptionPlan?.data?.id
        ? await updateSubscriptionPlan({
            ...savedSubscriptionPlan.data,
            subscriptionPlan: {
              id: Number(data.subscriptionPlanId),
              planName: subscriptionPlans?.data?.find(
                subscriptionPlan =>
                  subscriptionPlan.id === Number(data.subscriptionPlanId)
              )?.planName,
            },
          }).unwrap()
        : await createSubscriptionPlan({
            companyBasicInfo: {
              id: Number(companyId),
            },
            subscriptionPlan: {
              id: Number(data.subscriptionPlanId),
            },
          }).unwrap();

      // core-api reports business failures INSIDE a 200 body, so `.unwrap()`
      // resolves on them: classify the envelope (errorCode, canonical) before
      // treating this as saved. Body failures now reach the same error toast
      // that only transport failures used to reach.
      const failure = getBodyFailureMessage(result);
      if (failure) {
        toast({
          title: 'Could not save subscription plan',
          description: failure,
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
        return;
      }

      toast({
        title: 'Subscription plan saved',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      navigateToPreviousPage();
    } catch (error) {
      console.error(error);
      toast({
        title: 'Could not save subscription plan',
        description: getApiErrorMessage(error),
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  }

  if (isGetSubscriptionPlansLoading || isSubscriptionPlanLoading)
    return <CenteredSpinner />;

  return (
    <Box>
      <HStack justify="space-between" mt="4" mb="12" align="start">
        <Heading color="custom_blue.1" fontSize="2xl">
          Subscription Plan
        </Heading>
      </HStack>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        <FormControl isInvalid={!!errors.subscriptionPlanId} flexBasis="70%">
          <Controller
            control={control}
            name="subscriptionPlanId"
            rules={{ required: 'Company size is required' }}
            render={({ field }) => (
              <FormControl>
                <RadioGroup
                  {...field}
                  display="flex"
                  justifyContent="space-between"
                >
                  {subscriptionPlans?.data.map(({ id, planName }) => (
                    <Radio
                      value={String(id)}
                      key={id}
                      mr="4"
                      colorScheme="orange"
                    >
                      {planName} Subscription Plan
                    </Radio>
                  ))}
                </RadioGroup>
              </FormControl>
            )}
          />
          <FormErrorMessage>
            {errors.subscriptionPlanId && errors.subscriptionPlanId.message}
          </FormErrorMessage>
        </FormControl>

        <HStack mt={4} justify="end">
          <Button onClick={navigateToPreviousPage}>Cancel</Button>
          <Button
            isLoading={isSubmitting}
            type="submit"
            bg="custom_orange.2"
            _hover={{ bg: 'custom_orange.2' }}
            color="white"
            disabled={isValid}
          >
            Save
          </Button>
        </HStack>
      </form>
    </Box>
  );
};
